import React from 'react';
import PropTypes from 'prop-types';
import { FormattedHTMLMessage, FormattedMessage, FormattedNumber } from 'react-intl';
import Field from 'redux-form/es/Field';
import { getCurrencyCode } from 'utils/helpers';
import InputField from 'components/Credit24/Core/Controls/InputField';
import InputToggleGroup from 'components/Credit24/Core/Controls/InputToggleGroup';
import CheckboxInput from 'components/Credit24/Core/Controls/CheckboxInput';
import InputSelectField from 'components/Credit24/Core/Controls/InputSelectField';
import { RENT, BOARDING, OWN_MORTGAGE } from 'consts';
import { parseAmount } from 'consts/loan/AU/expenses';

const propTypes = {
  incomeFrequencies: PropTypes.array.isRequired,
  residenceTypes: PropTypes.array.isRequired,
  residentialStatus: PropTypes.string,
  nonEssentialExpenses: PropTypes.string,
  totalLivingExpenses: PropTypes.string,
};

const accommodationWithCosts = [BOARDING, RENT];

const ExpensesAU = ({
  incomeFrequencies,
  residenceTypes,
  residentialStatus,
  nonEssentialExpenses,
  totalLivingExpenses,
}) => {
  const numericTotalLivingExpenses = parseAmount(totalLivingExpenses);
  const showAccommodationCosts = accommodationWithCosts.includes(residentialStatus);
  const showHint = residentialStatus === OWN_MORTGAGE;
  const hasNonEssentialExpenses = parseAmount(nonEssentialExpenses) > 0;

  return (
    <>
      <p className="loan-form__step-subtitle info block">
        <FormattedMessage id="expenses.info" />
      </p>
      <div className="loan-form__step-body">
        <h3 className="loan-form__step-subtitle">
          <FormattedHTMLMessage id="expenses.subtitle" />
        </h3>
        <Field
          emptyTextKey="expenses.residentialStatus"
          name="residentialStatus"
          component={InputSelectField}
          options={residenceTypes}
          isFullWidth
        />
        {showHint && (
          <p className="hint">
            <FormattedMessage id="expenses.hintMessage" />
          </p>
        )}
        {residentialStatus && (
          <>
            {showAccommodationCosts && (
              <div className="grid">
                <div className="grid__item one-half palm-one-whole">
                  <Field
                    title={<FormattedMessage id="expenses.accommodationCosts.title" />}
                    name="housingCosts"
                    component={InputField}
                    prefix="$"
                    useDigitsGrouping
                    isFullWidth
                    isErrorVisible
                    hintConfig={{ titleKey: 'expenses.accommodationCosts.error' }}
                  />
                </div>
                <div className="grid__item one-half palm-one-whole">
                  <InputToggleGroup
                    name="housingCostsFrequency"
                    list={incomeFrequencies}
                  />
                </div>
              </div>
            )}
            <div className="living-expenses">
              <dl className="living-expenses__total">
                <dt className="living-expenses__total-title">
                  <FormattedMessage id="expenses.calculator.title" tagName="h3" />
                </dt>
                <dd className="living-expenses__total-details">
                  <FormattedNumber
                    minimumFractionDigits={2}
                    maximumFractionDigits={2}
                    currency={getCurrencyCode()}
                    style="currency"
                    value={numericTotalLivingExpenses}
                  />
                </dd>
              </dl>
              <div className="grid">
                <div className="grid__item one-half palm-one-whole">
                  <Field
                    title={<FormattedMessage id="expenses.essential.title" />}
                    name="essentialExpenses"
                    component={InputField}
                    prefix="$"
                    useDigitsGrouping
                    isFullWidth
                    hintConfig={{ titleKey: 'expenses.essential.error' }}
                  />
                </div>
                <div className="grid__item one-half palm-one-whole" id="essentialExpensesFrequency">
                  <InputToggleGroup
                    name="essentialExpensesFrequency"
                    list={incomeFrequencies}
                  />
                </div>
                <p className="grid__item hint">
                  <FormattedHTMLMessage id="expenses.essential.hint" />
                </p>
              </div>
              <div className="grid">
                <div className="grid__item one-half palm-one-whole">
                  <Field
                    title={<FormattedMessage id="expenses.nonessential.title" />}
                    name="nonEssentialExpenses"
                    component={InputField}
                    prefix="$"
                    useDigitsGrouping
                    isFullWidth
                  />
                </div>
                <div className="grid__item one-half palm-one-whole" id="nonEssentialExpensesFrequency">
                  <InputToggleGroup
                    name="nonEssentialExpensesFrequency"
                    list={incomeFrequencies}
                  />
                </div>
                <p className="grid__item hint">
                  <FormattedMessage id="expenses.nonessential.hint" />
                </p>
              </div>
              {hasNonEssentialExpenses && (
                <div className="info">
                  <Field
                    name="willForgoNonEssentials"
                    component={CheckboxInput}
                    text={<FormattedMessage id="expenses.nonessential.checkbox" />}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

ExpensesAU.propTypes = propTypes;
export default ExpensesAU;
